<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'shared/components/Spinner.vue';

// Status indicator + reconnect control for an existing uazapi inbox, shown per
// row in the inbox list. Reconnecting only re-pairs the SAME instance (QR or
// pairing code) — it never creates a new inbox/instance and never touches
// existing conversations. Credentials are resolved server-side.
const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const status = ref('loading'); // loading | connected | disconnected | error
const phone = ref('');
const show = ref(false);
const mode = ref(''); // '' (choose) | qr | code | done
const qrCodeData = ref('');
const pairCode = ref('');
const loading = ref(false);
const timer = ref(null);

function proxyBaseUrl() {
  const accountId = window.location.pathname.split('/')[3];
  return `/api/v1/accounts/${accountId}/uazapi_proxy`;
}

const dotClass = computed(
  () =>
    ({
      loading: 'bg-n-slate-6',
      connected: 'bg-n-teal-9',
      disconnected: 'bg-n-ruby-9',
      error: 'bg-n-amber-9',
    })[status.value]
);

const statusTooltip = computed(() =>
  status.value === 'connected'
    ? t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.STATUS_CONNECTED')
    : t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.STATUS_DISCONNECTED')
);

function stopTimer() {
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
}

function onConnected() {
  stopTimer();
  status.value = 'connected';
  mode.value = 'done';
  useAlert(t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.SUCCESS'));
}

function detectConnected(data) {
  return (
    data?.connected === true ||
    data?.status?.connected === true ||
    data?.status?.loggedIn === true ||
    data?.instance?.status === 'connected'
  );
}

function applyResponse(data) {
  if (detectConnected(data)) {
    onConnected();
    return;
  }
  if (mode.value === 'code') {
    pairCode.value =
      data.paircode ||
      data.pairingCode ||
      data.code ||
      data.instance?.paircode ||
      pairCode.value;
  } else {
    qrCodeData.value =
      data.qrcode ||
      data.instance?.qrcode ||
      data.base64 ||
      data.qr ||
      data.instance?.base64 ||
      qrCodeData.value;
  }
}

async function requestConnect() {
  const { data } = await window.axios.post(`${proxyBaseUrl()}/inbox_connect`, {
    inbox_id: props.inbox.id,
    mode: mode.value,
    phone: phone.value,
  });
  applyResponse(data);
}

async function fetchStatus() {
  try {
    const { data } = await window.axios.get(`${proxyBaseUrl()}/inbox_status`, {
      params: { inbox_id: props.inbox.id },
    });
    if (!data.uazapi) {
      status.value = 'error';
      return;
    }
    phone.value = data.phone || '';
    status.value = data.connected ? 'connected' : 'disconnected';
  } catch {
    status.value = 'error';
  }
}

function startPolling() {
  stopTimer();
  timer.value = setInterval(async () => {
    await fetchStatus();
    if (status.value === 'connected') {
      onConnected();
    } else {
      await requestConnect().catch(() => {});
    }
  }, 15000);
}

async function startConnect(selectedMode) {
  mode.value = selectedMode;
  loading.value = true;
  qrCodeData.value = '';
  pairCode.value = '';
  try {
    await requestConnect();
    startPolling();
  } catch {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.ERROR'));
  } finally {
    loading.value = false;
  }
}

function open() {
  show.value = true;
  mode.value = '';
  qrCodeData.value = '';
  pairCode.value = '';
}

function close() {
  show.value = false;
  stopTimer();
}

onMounted(fetchStatus);
onBeforeUnmount(stopTimer);
</script>

<template>
  <div class="flex items-center gap-2">
    <span
      v-tooltip.top="statusTooltip"
      class="size-2.5 rounded-full flex-shrink-0"
      :class="dotClass"
    />
    <NextButton
      v-tooltip.top="$t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.BUTTON')"
      icon="i-lucide-refresh-cw"
      slate
      sm
      @click="open"
    />

    <woot-modal v-model:show="show" :on-close="close">
      <div class="flex flex-col items-center text-center p-8 gap-4">
        <h3 class="text-lg font-semibold text-n-slate-12 m-0">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 m-0 max-w-sm">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.SAFE_NOTE') }}
        </p>

        <!-- Connected -->
        <div
          v-if="mode === 'done' || status === 'connected'"
          class="flex flex-col items-center gap-3 py-4"
        >
          <div
            class="flex items-center justify-center size-14 rounded-full bg-green-100 dark:bg-green-900/30"
          >
            <Icon
              icon="i-lucide-check-circle"
              class="size-7 text-green-600 dark:text-green-400"
            />
          </div>
          <p class="text-base font-medium text-n-slate-12 m-0">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.CONNECTED_TITLE') }}
          </p>
          <p v-if="phone" class="text-sm text-n-slate-11 m-0">{{ phone }}</p>
          <NextButton
            blue
            class="mt-2"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.CLOSE')"
            @click="close"
          />
        </div>

        <!-- Choose method -->
        <div
          v-else-if="mode === ''"
          class="flex flex-col gap-3 w-full max-w-xs"
        >
          <p class="text-sm font-medium text-n-slate-12 m-0">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.CHOOSE_METHOD') }}
          </p>
          <NextButton
            blue
            icon="i-lucide-qr-code"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.QR_OPTION')"
            @click="startConnect('qr')"
          />
          <NextButton
            faded
            blue
            icon="i-lucide-keyboard"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.CODE_OPTION')"
            @click="startConnect('code')"
          />
        </div>

        <!-- QR -->
        <div v-else-if="mode === 'qr'" class="flex flex-col items-center gap-3">
          <div
            v-if="qrCodeData"
            class="p-4 bg-white rounded-xl border border-n-weak shadow-sm"
          >
            <img
              :src="
                qrCodeData.startsWith('data:')
                  ? qrCodeData
                  : `data:image/png;base64,${qrCodeData}`
              "
              alt="QR Code"
              class="size-64"
            />
          </div>
          <div
            v-else
            class="flex items-center justify-center size-64 bg-n-alpha-2 rounded-xl"
          >
            <Spinner />
          </div>
          <p class="text-sm text-n-slate-11 m-0 max-w-sm">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.QR_HELP') }}
          </p>
          <button
            class="text-sm text-n-slate-11 hover:text-n-slate-12 underline cursor-pointer"
            @click="mode = ''"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.BACK') }}
          </button>
        </div>

        <!-- Pairing code -->
        <div
          v-else-if="mode === 'code'"
          class="flex flex-col items-center gap-3"
        >
          <p class="text-sm font-medium text-n-slate-12 m-0">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.CODE_TITLE') }}
          </p>
          <div
            v-if="pairCode"
            class="px-6 py-4 bg-n-alpha-2 rounded-xl text-2xl font-bold tracking-[0.3em] text-n-slate-12"
          >
            {{ pairCode }}
          </div>
          <div
            v-else
            class="flex items-center justify-center px-6 py-4 bg-n-alpha-2 rounded-xl"
          >
            <Spinner />
          </div>
          <p class="text-sm text-n-slate-11 m-0 max-w-sm">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.CODE_HELP') }}
          </p>
          <button
            class="text-sm text-n-slate-11 hover:text-n-slate-12 underline cursor-pointer"
            @click="mode = ''"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.RECONNECT.BACK') }}
          </button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
